"use client";

import { useState } from "react";
import { EMBED_LINK_REL } from "@/app/widgets/embed-attribution";

interface KundaliWidgetCardProps {
  language?: "en" | "hi";
  theme?: "light" | "dark";
}

const FIELD_CLASS =
  "w-full rounded-lg border border-border-default dark:border-white/15 bg-surface-canvas dark:bg-ish-surface px-3 py-2 text-text-primary dark:text-ish-cream focus:outline-none focus:ring-1 focus:ring-intent-brand";

const LABEL_CLASS = "block font-medium mb-1 text-text-secondary dark:text-white/80";

export default function KundaliWidgetCard({
  language = "en",
  theme = "light",
}: KundaliWidgetCardProps) {
  const isHi = language === "hi";
  const isDark = theme === "dark";

  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [tob, setTob] = useState("12:00");
  const [city, setCity] = useState("New Delhi");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const query = new URLSearchParams({
      name: name || (isHi ? "जातक" : "Seeker"),
      dob,
      tob,
      city,
    }).toString();
    window.open(
      `https://ishvaram.com/${isHi ? "hi/free-kundali" : "free-kundali"}?${query}`,
      "_blank"
    );
  };

  return (
    <div
      className={`mx-auto w-full max-w-[440px] rounded-xl border p-5 font-sans shadow-sm transition-colors ${
        isDark
          ? "border-white/10 bg-surface-dark text-ish-cream"
          : "border-border-subtle bg-surface-raised text-text-primary"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-3 border-border-subtle dark:border-white/10">
        <div className="flex items-center gap-2">
          <span className="text-intent-brand dark:text-ritual-gold font-serif font-bold text-lg">
            ॐ
          </span>
          <div>
            <h2 className="text-sm font-bold tracking-tight">
              {isHi ? "मुफ्त जन्म कुंडली कैलकुलेटर" : "Free Janam Kundali Calculator"}
            </h2>
            <p className="text-caption text-text-muted dark:text-white/60">
              {isHi
                ? "सटीक वैदिक लग्न एवं ग्रह स्थिति"
                : "Accurate Vedic Lagna & Planetary Positions"}
            </p>
          </div>
        </div>
      </div>

      {/* Interactive Form */}
      <form onSubmit={handleSubmit} className="my-4 space-y-3 text-xs">
        <div>
          <label className={LABEL_CLASS}>{isHi ? "नाम" : "Full Name"}</label>
          <input
            type="text"
            required
            placeholder={isHi ? "उदा. राहुल शर्मा" : "e.g. Rahul Sharma"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={FIELD_CLASS}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={LABEL_CLASS}>{isHi ? "जन्म तिथि" : "Date of Birth"}</label>
            <input
              type="date"
              required
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className={FIELD_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>{isHi ? "जन्म समय" : "Time of Birth"}</label>
            <input
              type="time"
              required
              value={tob}
              onChange={(e) => setTob(e.target.value)}
              className={FIELD_CLASS}
            />
          </div>
        </div>

        <div>
          <label className={LABEL_CLASS}>{isHi ? "जन्म स्थान / शहर" : "Birth Place / City"}</label>
          <input
            type="text"
            required
            placeholder={isHi ? "उदा. नई दिल्ली, मुंबई" : "e.g. New Delhi, Mumbai"}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className={FIELD_CLASS}
          />
        </div>

        <button
          type="submit"
          className="w-full mt-2 rounded-lg bg-surface-brand-strong hover:bg-surface-brand-strong-hover text-text-on-brand font-semibold py-2.5 shadow-sm transition-colors text-xs"
        >
          {isHi ? "कुंडली बनाएं (Generate Kundali) →" : "Generate Kundali Chart →"}
        </button>
      </form>

      {/* Attribution Footer */}
      <div className="flex items-center justify-between border-t pt-2.5 border-border-subtle dark:border-white/10 text-caption">
        <a
          href={
            isHi ? "https://ishvaram.com/hi/free-kundali/" : "https://ishvaram.com/free-kundali/"
          }
          target="_blank"
          rel={EMBED_LINK_REL}
          className="inline font-medium text-intent-brand hover:text-text-primary dark:text-ritual-gold dark:hover:text-ish-cream underline underline-offset-2 transition-colors"
        >
          {isHi ? "मुफ्त कुंडली एवं महादशा →" : "Free Kundali & Mahadasha →"}
        </a>
        <span className="text-caption text-text-muted dark:text-white/50">
          Powered by{" "}
          <a
            href="https://ishvaram.com/"
            target="_blank"
            rel={EMBED_LINK_REL}
            className="inline hover:underline font-semibold text-text-secondary dark:text-white/60"
          >
            Ishvaram
          </a>
        </span>
      </div>
    </div>
  );
}
